import React from "react";
import moment from "moment-timezone";
import { observer } from "mobx-react-lite";
import objectHash from "object-hash";
import { Layout } from "../../layout";
import {
  ChargeableItem,
  ChartData,
  ChartUnit,
  featureLabelMap,
  formatXLabel,
  getUsageRequestBody,
  getUsageDayPreset,
  getUsageHourPresets,
  getUsageMonthPreset,
  getUsageWeekPreset,
  GraphData,
  GroupBy,
  PageView,
  Period,
  PeriodGroup,
  TimeStep,
  UsageRequestBody,
  OrderedArray,
  IEC_Helpers,
  DECIMAL_Helpers,
  DECIMAL_UNITS,
  DECIMAL_UNIT,
  IEC_UNITS,
  IEC_UNIT,
  formatXLabelSpreadsheet,
  OnMouseMoveParam,
} from "./utils";
import { Dropdown, Menu, Select, Slider, notification } from "antd";
import { Bar, CartesianGrid, XAxis, YAxis, Legend, BarChart } from "recharts";
import { Loader } from "../../components/layout/loader";
import { ArrowRight, Check, Circle, X } from "react-feather";
import { arraysAreEqual, request } from "../../services/cpln";
import { FormLabel } from "../../components/forms/formLabel";
import { darkThemeColors, lightThemeColors } from "./colors";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ConsoleContext } from "../../mobxStores/consoleContext/consoleContext";
import { Theme } from "../../mobxStores/uiData/theme";
import { v4 as uuidv4 } from "uuid";
import { CustomLegend } from "./legend";
import { LegendPayloadSaver } from "./legendPayloadSaver";
import { useDebounce } from "../../components/table/useDebounce";
import { ViewModal } from "../../components/modals/viewModal";
import { BillingContext } from "../../mobxStores/billingContext/billingContext";
import { NGButton } from "../../newcomponents/button/Button";
import { ChevronDown, ChevronUp } from "react-feather";
import NGAlert from "../../newcomponents/alert";
import { RangePicker } from "../../components/antd/RangePicker";
import { Timezone } from "../../mobxStores/userData/timezone";
import { sortBy, groupBy as lodashGroupBy } from "lodash";
import { NGCheckbox } from "../../newcomponents/checkbox";
import { getBillingErrorMessage } from "../../services/utils";
import { InfoTooltip } from "../../components/InfoTooltip";
import { UsageSpreadsheetView } from "./spreadsheet";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import numeral from "numeral";
import { CardTooltip } from "./cardTooltip";

const UsageNGRaw: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mounted, setMounted] = React.useState(false);

  const cItems = BillingContext.ChargeableItems;
  const features = BillingContext.ChargeableItemsFeatures;
  const [theme, setTheme] = React.useState(Theme.theme);
  React.useEffect(() => {
    setTheme(Theme.theme);
  }, [Theme.theme]);

  const navigate = useNavigate();

  const requestIdRef = React.useRef(uuidv4());

  const palette = React.useMemo(() => {
    return theme === "light" ? lightThemeColors : darkThemeColors;
  }, [theme]);

  const { org } = ConsoleContext;
  // for having a link to billing
  const [accountId, setAccountId] = React.useState<string | null>(null);

  const [isViewing, setIsViewing] = React.useState(false);
  const [isLoadingViewExport, setIsLoadingViewExport] = React.useState(false);

  const [activeBarIndex, setActiveBarIndex] = React.useState<number | null>(null);
  const [tooltipData, setTooltipData] = React.useState<OnMouseMoveParam | null>(null);

  const [isFiltersOpened, setIsFiltersOpened] = React.useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = React.useState(false);
  React.useEffect(() => {
    if (isFiltersOpen) {
      setIsFiltersOpened(true);
    }
  }, [isFiltersOpen]);

  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const [legendPayload, _setLegendPayload] = React.useState<any[] | undefined>([]);
  const [legendFilters, setLegendFilters] = React.useState<string[]>([]);
  function toggleLegendFilter(value: string, reverse: boolean) {
    if (legendFilters.includes(value)) {
      setLegendFilters(legendFilters.filter((legendFilter) => legendFilter !== value));
    } else {
      setLegendFilters([...legendFilters, value]);
    }
  }

  function getDefaultTimeStep(): TimeStep {
    let res: TimeStep = "day";
    try {
      const data = searchParams.get("data") as any;
      const dataObject = JSON.parse(data);
      res = dataObject.timeStep;
    } catch (e) {}
    return res;
  }
  const [timeStep, setTimeStep] = React.useState<TimeStep>(getDefaultTimeStep());
  const defaultPageView = "cost";
  const [pageView, setPageView] = React.useState<PageView>(defaultPageView);
  function getDefaultMetricValue() {
    try {
      return getMetricOptions()[0].options[0].value;
    } catch (e) {
      return "";
    }
  }
  const [metric, setMetric] = React.useState<string>(getDefaultMetricValue());
  const chargeableItemOfMetric = React.useMemo(() => {
    if (pageView === "metric") {
      return BillingContext.ChargeableItems.find((ci) => ci.name === metric);
    }
    return undefined;
  }, [metric, pageView]);
  const defaultGroupBy = "org";
  const [groupBy, setGroupBy] = React.useState<GroupBy>(defaultGroupBy);
  const [queryResponseKey, setQueryResponseKey] = React.useState<string>(uuidv4());
  const [queryResponse, setQueryResponse] = React.useState<any>(null);
  const [queryResponseCache, setQueryResponseCache] = React.useState<any>(null);

  const [shouldAddVolumesetCost, setShouldAddVolumesetCost] = React.useState(true);

  // for adding volumeset cost to workloads
  const [secondaryQueryResponse, setSecondaryQueryResponse] = React.useState<any>(null);

  const [allGVCs, setAllGVCs] = React.useState<string[]>([]);
  const [gvcFilters, setGVCFilters] = React.useState<string[]>([]);
  const [allLocationss, setAllLocations] = React.useState<string[]>([]);
  const [locationFilters, setLocationFilters] = React.useState<string[]>([]);
  const [allTags, setAllTags] = React.useState<{ [tag: string]: string[] }>({});
  const [tagFilters, setTagFilters] = React.useState<{ [tag: string]: string[] }>({});

  const defaultItemCount = 50;
  const [topItemCount, setTopItemCount] = React.useState<number | null>(defaultItemCount);
  const [topItemCountRaw, setTopItemCountRaw] = React.useState<number | null>(defaultItemCount);
  const [isInGridView, setIsInGridView] = React.useState<boolean>(false);

  const [chargeableItemFilters, setChargeableItemFilters] = React.useState<string[]>([]); // use id as value
  const [featureFilters, setFeatureFilters] = React.useState<string[]>([]);

  // TODO support without null
  function getDefaultStartISO() {
    let res = moment().startOf("day").subtract(31, "day").toISOString();
    try {
      const data = searchParams.get("data") as any;
      const dataObject = JSON.parse(data);
      res = dataObject.startISO;
    } catch (e) {}
    return res;
  }
  const [startISO, setStartISO] = React.useState<string | null>(getDefaultStartISO());
  function getDefaultEndISO() {
    let res = moment().startOf("day").toISOString();
    try {
      const data = searchParams.get("data") as any;
      const dataObject = JSON.parse(data);
      res = dataObject.endISO;
    } catch (e) {}
    return res;
  }
  const [endISO, setEndISO] = React.useState<string | null>(getDefaultEndISO());

  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Initialize values from the url query
  React.useEffect(() => {
    const data = searchParams.get("data");
    if (!data) {
      setMounted(true);
      return;
    }

    const dataObject = JSON.parse(data);

    setPageView(dataObject.pageView || defaultPageView);
    setGroupBy(dataObject.groupBy || defaultGroupBy);
    setMetric(dataObject.metric || getDefaultMetricValue());
    setLegendFilters(dataObject.legendFilters || []);
    setGVCFilters(dataObject.gvcFilters || []);
    setLocationFilters(dataObject.locationFilters || []);
    setTagFilters(dataObject.tagFilters || {});
    setChargeableItemFilters(dataObject.chargeableItemFilters || []);
    setFeatureFilters(dataObject.featureFilters || []);
    setShouldAddVolumesetCost(dataObject.shouldAddVolumesetCost === "false" ? false : true);
    setIsInGridView(dataObject.gridView === "true" ? true : false);

    setTimeout(() => {
      setMounted(true);
    }, 1);
  }, []);

  // Update url query when user updates filters
  React.useEffect(() => {
    if (!mounted) {
      return;
    }

    const _searchParams = new URLSearchParams();

    const paramObject: any = {
      timeStep: timeStep,
      pageView: pageView,
      groupBy: groupBy,
      metric: metric,
      startISO: startISO,
      endISO: endISO,
      gridView: String(isInGridView),
    };

    if (legendFilters.length > 0) {
      paramObject.legendFilters = legendFilters;
    }
    if (gvcFilters.length > 0) {
      paramObject.gvcFilters = gvcFilters;
    }
    if (locationFilters.length > 0) {
      paramObject.locationFilters = locationFilters;
    }
    let hasAnyTagFilter = false;
    const _tagFilters: { [tag: string]: string[] } = {};
    for (const [tag, filters] of Object.entries(tagFilters)) {
      if (filters.length > 0) {
        hasAnyTagFilter = true;
        _tagFilters[tag] = filters;
      }
    }
    if (hasAnyTagFilter) {
      paramObject.tagFilters = _tagFilters;
    }
    if (chargeableItemFilters.length > 0) {
      paramObject.chargeableItemFilters = chargeableItemFilters;
    }
    if (featureFilters.length > 0) {
      paramObject.featureFilters = featureFilters;
    }
    if (pageView === "cost" && groupBy === "workloads") {
      paramObject.shouldAddVolumesetCost = String(shouldAddVolumesetCost);
    }

    _searchParams.set("data", JSON.stringify(paramObject));
    setSearchParams(_searchParams);
  }, [
    mounted,
    timeStep,
    pageView,
    groupBy,
    metric,
    startISO,
    endISO,
    legendFilters,
    gvcFilters,
    locationFilters,
    tagFilters,
    chargeableItemFilters,
    featureFilters,
    shouldAddVolumesetCost,
    isInGridView,
  ]);

  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      // Check if the clicked element is outside the entire component
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsFiltersOpen(false);
      }
    };

    // Attach the event listener when the component mounts
    document.addEventListener("click", handleOutsideClick);

    // Detach the event listener when the component unmounts
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const handleDropdownOpenChange = (flag: boolean) => {
    setIsFiltersOpen(flag);
  };

  React.useEffect(() => {
    getAccountId();

    const propertyName = "--page-min-width";
    try {
      (document.querySelector(".n-page") as HTMLDivElement).style.setProperty(propertyName, "1650px");
    } catch (e) {
      console.error("Failed to update min width");
    }

    return () => {
      try {
        (document.querySelector(".n-page") as HTMLDivElement).style.setProperty(propertyName, "1400px");
      } catch (e) {
        console.error("Failed to reset min width");
      }
    };
  }, []);

  React.useEffect(() => {
    const onClick = (e: any) => setIsFiltersOpen(false);

    // TODONOW fix
    const elList = [".n-main", ".n-main-content", ".n-header"].map((className) => document.querySelector(className));
    elList.forEach((el) => el?.addEventListener("click", onClick));

    return () => elList.forEach((el) => el?.removeEventListener("click", onClick));
  }, []);

  React.useEffect(() => {
    if (!mounted) {
      return;
    }

    if (pageView === "metric") {
      setFeatureFilters([]);
      setChargeableItemFilters([]);
      if (!getMetricGroupByOptions().some((o) => o.value === "gvc")) {
        setGVCFilters([]);
      }
      if (!getMetricGroupByOptions().some((o) => o.value === "location")) {
        setLocationFilters([]);
      }
      const _tagFilters = JSON.parse(JSON.stringify(tagFilters));
      for (const feature of features) {
        if (!getMetricGroupByOptions().some((o) => o.value === feature)) {
          _tagFilters[feature] = [];
        }
      }
      setTagFilters(_tagFilters);

      if (!getMetricGroupByOptions().some((option) => option.value === groupBy)) {
        setGroupBy("org");
      }
    }
  }, [pageView, groupBy, metric]);

  const [tagValuesTriggererDebounced, setTagValuesTriggererDebounced] = useDebounce(uuidv4(), 400);
  function triggerTagValues() {
    setTagValuesTriggererDebounced(uuidv4());
  }
  React.useEffect(() => {
    fetchAllTags();
  }, [tagValuesTriggererDebounced]);
  React.useEffect(() => {
    if (!mounted) {
      return;
    }

    triggerTagValues();
  }, [startISO, endISO]);

  const [gvcRelatedTagValuesTriggererDebounced, setGvcRelatedTagValuesTriggererDebounced] = useDebounce(uuidv4(), 400);
  function triggerGvcRelatedTagValues() {
    setGvcRelatedTagValuesTriggererDebounced(uuidv4());
  }
  React.useEffect(() => {
    fetchGvcRelatedTags();
  }, [gvcRelatedTagValuesTriggererDebounced]);
  React.useEffect(() => {
    if (!mounted) {
      return;
    }

    triggerGvcRelatedTagValues();
  }, [gvcFilters]);

  async function fetchAllTags() {
    const gvcTagsPromise = fetchAllItemsOfKind("gvc");
    const locationTagsPromise = fetchAllItemsOfKind("location");
    const featureTagsPromises = features.map((f) => fetchAllItemsOfKind("name", { feature: f }));
    const responses = await Promise.all([gvcTagsPromise, locationTagsPromise, ...featureTagsPromises]);
    const _tags: { [tag: string]: string[] } = {};
    setAllGVCs(responses[0].filter(Boolean));
    setAllLocations(responses[1].filter(Boolean));
    features.map((feature, index) => (_tags[feature] = responses[index + 2].filter(Boolean)));
    setAllTags(_tags);
  }

  async function fetchGvcRelatedTags() {
    const featureTagsPromises = ["workloads", "volume-sets"].map((f) => fetchAllItemsOfKind("name", { feature: f }));
    const responses = await Promise.all(featureTagsPromises);
    const workloadTags = responses[0].filter(Boolean);
    const volumesetTags = responses[1].filter(Boolean);
    const _allTags = JSON.parse(JSON.stringify(allTags));
    _allTags["workloads"] = workloadTags;
    _allTags["volume-sets"] = volumesetTags;
    setAllTags(_allTags);
  }

  async function fetchAllItemsOfKind(tag: string, filterBy?: any) {
    const body: any = {
      startTime: moment(startISO).startOf("day").toISOString(),
      endTime: moment(endISO).startOf("day").toISOString(),
      tag: tag,
    };
    if (filterBy) {
      body.filterBy = filterBy;
    }
    if (
      filterBy &&
      filterBy.feature &&
      ["workloads", "volume-sets"].includes(filterBy.feature) &&
      gvcFilters.length > 0
    ) {
      if (!body.filterBy) {
        body.filterBy = {};
      }
      body.filterBy.gvc = `/${gvcFilters.map((i) => `^${i}$`).join("|")}/`;
    }
    const { data } = await request({
      service: "metering",
      method: "post",
      url: `/org/${ConsoleContext.org}/tags/values/query`,
      body: body,
    });
    return data.tagValues || [];
  }

  async function getAccountId() {
    try {
      const { data } = await request({ service: "billing-ng", url: `/org/${org}/account` });
      setAccountId(data.id);
    } catch (e) {
      console.error("account id of org is not found");
    }
  }

  const [queryTriggererDebounced, setQueryTriggererDebounced] = useDebounce(uuidv4(), 400);
  function triggerQuery() {
    setLegendFilters([]);
    setQueryResponse({});
    setSecondaryQueryResponse({});
    setQueryTriggererDebounced(uuidv4());
  }

  React.useEffect(() => {
    queryUsage();
  }, [queryTriggererDebounced]);

  React.useEffect(() => {
    if (!mounted) {
      return;
    }

    triggerQuery();
  }, [mounted, timeStep, pageView, metric, groupBy, startISO, endISO]);

  const filtersHash = React.useMemo(() => {
    return objectHash({
      gvcFilters,
      locationFilters,
      tagFilters,
      chargeableItemFilters,
      featureFilters,
    });
  }, [gvcFilters, locationFilters, tagFilters, chargeableItemFilters, featureFilters]);
  const [lastFiltersHash, setLastFiltersHash] = React.useState(filtersHash);

  React.useEffect(() => {
    if (!mounted) {
      return;
    }
    if (isFiltersOpen) {
      return;
    }
    if (!isFiltersOpened) {
      return;
    }
    if (filtersHash === lastFiltersHash) {
      return;
    }

    triggerQuery();
  }, [isFiltersOpen]);

  React.useEffect(() => {
    if (!mounted) {
      return;
    }
    if (isFiltersOpen) {
      return;
    }
    if (!isFiltersOpened) {
      return;
    }

    setTopItemCount(topItemCountRaw);
  }, [isFiltersOpen]);

  const queryRequestBodiesRaw: { body: UsageRequestBody; vsBody: UsageRequestBody | undefined } = React.useMemo(() => {
    let bodyStartTime = startISO!;
    let bodyEndTime = endISO!;
    if (timeStep !== "hour") {
      bodyStartTime = moment(startISO).startOf("day").toISOString();
      bodyEndTime = moment(endISO).startOf("day").toISOString();
    }
    // Fixing the lib bug here by rounding times always
    bodyStartTime = moment.utc(bodyStartTime).startOf("day").toISOString();
    bodyEndTime = moment.utc(bodyEndTime).startOf("day").add(1, "day").toISOString();

    const body = getUsageRequestBody(bodyStartTime, bodyEndTime, timeStep, pageView, groupBy, cItems, features);
    for (const query of body.consumptionQueries) {
      if (pageView === "cost" || getMetricGroupByOptions().some((o) => o.value === "gvc")) {
        if (gvcFilters.length > 0) {
          (query.filterBy as any).gvc = `/${gvcFilters.map((i) => `^${i}$`).join("|")}/`;
        }
      }
      if (pageView === "cost" || getMetricGroupByOptions().some((o) => o.value === "location")) {
        if (locationFilters.length > 0) {
          (query.filterBy as any).location = `/${locationFilters.map((i) => `^${i}$`).join("|")}/`;
        }
      }
      for (const [tag, values] of Object.entries(tagFilters)) {
        if (values.length < 1) {
          continue;
        }
        if ((query.filterBy as any).feature !== tag) {
          continue;
        }

        if (pageView === "cost" || getMetricGroupByOptions().some((o) => o.value === tag)) {
          (query.filterBy as any).name = `/${values.map((i) => `^${i}$`).join("|")}/`;
        }
      }
    }

    // START include volumeset costs for workloads
    let vsBody: UsageRequestBody = undefined as any;
    if (pageView === "cost" && groupBy === "workloads") {
      vsBody = JSON.parse(JSON.stringify(body));
      vsBody.consumptionQueries = vsBody.consumptionQueries.filter((q) => q.filterBy.feature === "volume-sets");
      const sharedVolumesetChargeableItemIds = BillingContext.ChargeableItems.filter((ci) =>
        ci.name.toLowerCase().includes("shared"),
      ).map((ci) => ci.id);
      vsBody.consumptionQueries = vsBody.consumptionQueries.filter(
        (q) => !sharedVolumesetChargeableItemIds.includes(q.id),
      );
      const volumesetSnapshotChargeableItemIds = BillingContext.ChargeableItems.filter((ci) =>
        ci.name.toLowerCase().includes("snapshots"),
      ).map((ci) => ci.id);
      vsBody.consumptionQueries = vsBody.consumptionQueries.filter(
        (q) => !volumesetSnapshotChargeableItemIds.includes(q.id),
      );
      for (const consumptionQuery of vsBody.consumptionQueries) {
        if (!consumptionQuery.aggregateBy.includes("workload")) {
          consumptionQuery.aggregateBy.push("workload");
        }
        if (!consumptionQuery.aggregateBy.includes("gvc")) {
          consumptionQuery.aggregateBy.push("gvc");
        }
      }
      if (!vsBody.groupBy.includes("gvc")) {
        vsBody.groupBy.push("gvc");
      }
    }
    // END

    if (pageView === "cost") {
      if (features.includes(groupBy)) {
        body.consumptionQueries = body.consumptionQueries.filter((q) => q.filterBy.feature === groupBy);
      }
      if (chargeableItemFilters.length > 0) {
        body.consumptionQueries = body.consumptionQueries.filter((q) => chargeableItemFilters.includes(q.id));
      }
      if (featureFilters.length > 0) {
        body.consumptionQueries = body.consumptionQueries.filter((q) => featureFilters.includes(q.filterBy.feature));
      }
    }
    if (pageView === "metric") {
      let chargeableItem: ChargeableItem | undefined = BillingContext.ChargeableItems.find((ci) => {
        const name = metric;
        return ci.name === name;
      });

      if (!chargeableItem) {
        console.error(`Selected metric ${metric} but there is no chargeable item with this name in Billing Context`);
      } else {
        body.consumptionQueries = body.consumptionQueries.filter((q) => chargeableItem.id === q.id);
      }
    }
    if (["gvc", "location"].includes(groupBy)) {
      // for gvc and location, dont send mk8s and observability features
      const disallowedFeatures = ["mk8s", "observability"];
      const ids: string[] = BillingContext.ChargeableItems.filter((ci) => {
        if (disallowedFeatures.includes(ci.consumptionTags.feature)) {
          return true;
        }
        return false;
      }).map((ci) => ci.id);
      body.consumptionQueries = body.consumptionQueries.filter((q) => !ids.includes(q.id));
    }

    body.consumptionQueries = body.consumptionQueries.filter((cq) => Object.keys(cq.filterBy).length > 0);
    body.consumptionQueries = body.consumptionQueries.filter((cq) => cq.aggregateBy.length > 0);

    return { body: body, vsBody: vsBody };
  }, [
    startISO,
    endISO,
    timeStep,
    pageView,
    groupBy,
    featureFilters,
    chargeableItemFilters,
    gvcFilters,
    locationFilters,
    tagFilters,
  ]);

  const queryRequestBodies: { body: UsageRequestBody; vsBody: UsageRequestBody | undefined } = React.useMemo(() => {
    const { body: _body, vsBody: _vsBody } = queryRequestBodiesRaw;
    const body: UsageRequestBody = JSON.parse(JSON.stringify(_body));
    let vsBody: UsageRequestBody | undefined = undefined;
    if (_vsBody !== undefined) {
      vsBody = JSON.parse(JSON.stringify(_vsBody));
    }
    if (vsBody !== undefined) {
      for (const query of vsBody.consumptionQueries) {
        delete (query as any).id;
      }
    }
    for (const query of body.consumptionQueries) {
      delete (query as any).id;
    }
    return {
      body: body,
      vsBody: vsBody,
    };
  }, [queryRequestBodiesRaw]);

  async function queryUsage() {
    setLastFiltersHash(filtersHash);

    const requestId = uuidv4();
    requestIdRef.current = requestId;
    setIsLoading(true);
    setQueryResponse({});
    setSecondaryQueryResponse({});
    setError("");

    const { body, vsBody } = queryRequestBodies;

    try {
      const { data } = await request({
        service: "billing-ng",
        method: "post",
        url: `/org/${ConsoleContext.org}/charges/-query`,
        body: body,
      });
      if (requestIdRef.current !== requestId) {
        return;
      }

      setQueryResponse(data);
      setQueryResponseCache(data);
      setQueryResponseKey(uuidv4());
    } catch (e) {
      setIsLoading(false);
      const errorMessage = getBillingErrorMessage(e);
      setError(errorMessage);
      return;
    }

    try {
      if (vsBody) {
        const { data } = await request({
          service: "billing-ng",
          method: "post",
          url: `/org/${ConsoleContext.org}/charges/-query`,
          body: vsBody,
        });
        setSecondaryQueryResponse(data);
      }
    } catch (e) {}

    setIsLoading(false);
  }

  function setLegendPayload(newPayload?: any) {
    if (!newPayload || !Array.isArray(newPayload)) {
      _setLegendPayload(undefined);
      return;
    }

    const oldDataKeys = legendPayload?.map((p) => p.dataKey) || [];
    const newDataKeys = newPayload.map((p) => p.dataKey);

    if (arraysAreEqual(oldDataKeys, newDataKeys)) {
      return;
    }

    _setLegendPayload(newPayload);
  }

  function exportSpreadsheet() {
    try {
      const hasGvc = chartData.units.some((cdu) => {
        if (groupBy === "gvc") {
          return false;
        }
        return !!cdu.gvc;
      });

      const datakeys: string[] = [];
      const cdus: ChartUnit[] = [];
      for (const graphData of chartData.graphData) {
        let arr: OrderedArray = [];
        if (pageView === "cost") {
          if (groupBy === "workloads" && shouldAddVolumesetCost) {
            arr = graphData.secondaryTotalMapOrderedRaw;
          } else {
            arr = graphData.primaryTotalMapOrderedRaw;
          }
        } else {
          arr = graphData.valueMapOrderedRaw;
        }
        for (const arrItem of arr) {
          const dataKey = arrItem[1];
          if (datakeys.includes(dataKey)) {
            continue;
          }
          const cdu = chartData.units.find((cdu) => cdu.dataKey === dataKey);
          if (!cdu) {
            continue;
          }

          datakeys.push(dataKey);
          cdus.push(cdu);
        }
      }

      const workbook = XLSX.utils.book_new();
      const worksheet: XLSX.WorkSheet = {};

      let LAST_ROW = 1;
      let LAST_COL = 1;

      // Item Name Label
      let row = 0;
      let col = 0;
      let cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      worksheet[cellAddress] = {
        v: "Item Name",
        t: "s",
      };
      // Item names
      for (const cduIndexStr in cdus) {
        const cduIndex = Number(cduIndexStr);
        const cdu = cdus[cduIndex];

        if (!cdu.label) {
          continue;
        }

        // Item name
        let row = cduIndex + 1;
        let col = 0;
        if (row > LAST_ROW) {
          LAST_ROW = row;
        }
        let cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        worksheet[cellAddress] = {
          v: cdu.label,
          t: "s",
        };
      }

      if (hasGvc) {
        // GVC Label
        let row = 0;
        let col = 1;
        let cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        worksheet[cellAddress] = {
          v: "GVC Name",
          t: "s",
        };
        // Gvc names
        for (const cduIndexStr in cdus) {
          const cduIndex = Number(cduIndexStr);
          const cdu = cdus[cduIndex];

          if (!cdu.gvc) {
            continue;
          }

          // Gvc name
          let row = cduIndex + 1;
          let col = 1;
          if (row > LAST_ROW) {
            LAST_ROW = row;
          }
          let cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          worksheet[cellAddress] = {
            v: cdu.gvc,
            t: "s",
          };
        }
      }

      // costs/values of each time unit
      for (const graphDataIndexStr in chartData.graphData) {
        const graphDataIndex = Number(graphDataIndexStr);
        const graphData = chartData.graphData[graphDataIndex];

        let arr: OrderedArray = [];
        if (pageView === "cost") {
          if (groupBy === "workloads" && shouldAddVolumesetCost) {
            arr = graphData.secondaryTotalMapOrderedRaw;
          } else {
            arr = graphData.primaryTotalMapOrderedRaw;
          }
        } else {
          arr = graphData.valueMapOrderedRaw;
        }

        // time unit label
        let row = 0;
        let col = graphDataIndex + (hasGvc ? 2 : 1);
        if (col > LAST_COL) {
          LAST_COL = col;
        }
        let cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        worksheet[cellAddress] = {
          v: formatXLabelSpreadsheet(graphData.periodTimes, timeStep),
          t: "s",
        };

        // costs/values
        for (const cduIndexStr in cdus) {
          const cduIndex = Number(cduIndexStr);
          const cdu = cdus[cduIndex];

          const item = arr.find((arrItem) => arrItem[1] === cdu.dataKey);
          let value = 0;
          if (item && item[0] !== undefined && item[0] !== null) {
            value = item[0];
          }
          value = Number(value.toFixed(2));

          let row = cduIndex + 1;
          if (row > LAST_ROW) {
            LAST_ROW = row;
          }
          let col = graphDataIndex + (hasGvc ? 2 : 1);
          if (col > LAST_COL) {
            LAST_COL = col;
          }
          let cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          worksheet[cellAddress] = {
            v: value,
            t: "n",
          };
        }
      }

      if (pageView === "metric") {
        const row = LAST_ROW + 1;
        const col = 0;
        LAST_ROW = row;
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        worksheet[cellAddress] = {
          v: `Metric: ${metric} | Metric Unit: ${chartData.metricUnit}`,
          t: "s",
        };
      }

      worksheet["!ref"] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: LAST_ROW, c: LAST_COL },
      });

      XLSX.utils.book_append_sheet(workbook, worksheet, pageView === "cost" ? "Cost" : `Metric`);

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      let fileName = `${ConsoleContext.org}-${pageView}-${timeStep}${
        pageView === "metric" ? `-${metric}` : ""
      }-from-${startISO}-to-${endISO}-export-spreadsheet`;
      saveAs(blob, `${fileName}.xlsx`);
    } catch (e) {
      notification.warning({ message: "Failed", description: e.message });
    }
  }

  // TODO wont include volumeset cost right now
  async function exportJSON(detailed: boolean) {
    try {
      setIsLoadingViewExport(true);
      const a = document.createElement("a");
      a.style.display = "none";
      a.classList.add("cpln-temp-a");
      a.download = `usage-${org}-${endISO}-${timeStep}${detailed ? "-detailed" : ""}.json`;
      let blob = "";
      const queryBody = queryRequestBodies.body;
      if (detailed) {
        queryBody.detailed = true;
      }
      const url = `/org/${ConsoleContext.org}/charges/-query`;
      const { data } = await request({ service: "billing-ng", method: "post", url: url, body: queryBody });
      blob = JSON.stringify(data, null, 2);
      const file = new Blob([blob], { type: "text/json" });
      const href = URL.createObjectURL(file);
      a.href = href;
      a.click();
      setIsLoadingViewExport(false);
    } catch (e) {
      setIsLoadingViewExport(false);
      notification.warning({ message: "Failed", description: "Try again" });
    }
  }

  function drillDown() {
    if (timeStep === "hour") return;
    const label = tooltipData?.activeLabel;
    if (!label) return;
    if (timeStep === "month" || timeStep === "week") {
      setTimeStep("day");
    } else if (timeStep === "day") {
      setTimeStep("hour");
    }

    const startTime = moment(label.split(" ")[0]);
    const endTime = moment(label.split(" ")[1]);
    setStartISO(startTime.toISOString());
    setEndISO(endTime.toISOString());
  }

  // units are for each x axis group of graph items (bars in a single day)
  // graphData is the corresponding real data for the unit, by dataKey

  function dataKeyOrgGVCOf(
    _group: PeriodGroup,
    mergeMetrics = false,
    forAccount = false,
  ): { dataKey: string; org: string; gvc: string } {
    const group = JSON.parse(JSON.stringify(_group));

    if (forAccount) {
      delete group.key.org;
    }

    // will add up money, no need to separate datakeys by metric
    if (mergeMetrics || pageView === "metric") {
      delete group.key.metric;
    }

    const org = group.key.org || "";
    const gvc = group.key.gvc || "";
    delete group.key.org;
    delete group.key.gvc;

    let dataKey = Object.values(group.key).join("-");
    if (!!org) {
      let dataKeyOrgPart = "";
      if (!!dataKey) {
        dataKeyOrgPart += "-";
      }
      dataKeyOrgPart += org;
      dataKey += dataKeyOrgPart;
    }
    if (!!gvc) {
      let dataKeyGvcPart = "";
      if (!!dataKey) {
        dataKeyGvcPart += "-";
      }
      dataKeyGvcPart += gvc;
      dataKey += dataKeyGvcPart;
    }

    // happens when for account, only key is org and is deleted
    if (!dataKey) {
      dataKey = "org";
    }

    return {
      dataKey: dataKey,
      org: org,
      gvc: gvc,
    };
  }

  function labelOf(
    dataKey: string,
    org: string,
    gvc: string,
    pageView: PageView,
    groupBy: GroupBy,
    metric: string,
  ): string {
    if (groupBy === "org") {
      return org;
    }
    if (groupBy === "gvc") {
      return gvc;
    }
    let label = dataKey;
    // TODO might be problem if a workload/volume set name is same as the org or gvc, try and fix if creates issues
    if (gvc) {
      if (label.endsWith(`-${gvc}`)) {
        label = label.replace(`-${gvc}`, "");
      } else if (label.endsWith(`${gvc}`)) {
        label = label.replace(`${gvc}`, "");
      }
    }
    if (org) {
      if (label.endsWith(`-${org}`)) {
        label = label.replace(`-${org}`, "");
      } else if (label.endsWith(`${org}`)) {
        label = label.replace(`${org}`, "");
      }
    }
    return label;
  }

  function shouldMergeMetrics(pageView: PageView, groupBy: GroupBy) {
    return pageView === "cost" && groupBy !== "metric";
  }

  function shouldProcessForAccount(groupBy: GroupBy) {
    return groupBy === "account";
  }

  function unitOf(
    group: PeriodGroup,
    pageView: PageView,
    groupBy: GroupBy,
    metric: string,
  ): { dataKey: string; org: string; gvc: string; label: string } {
    const mergeMetrics = shouldMergeMetrics(pageView, groupBy);
    const forAccount = shouldProcessForAccount(groupBy);
    const { dataKey, org, gvc } = dataKeyOrgGVCOf(group, mergeMetrics, forAccount);
    const label = labelOf(dataKey, org, gvc, pageView, groupBy, metric);
    return {
      dataKey: dataKey,
      org: org,
      gvc: gvc,
      label: label,
    };
  }

  const periods: Period[] = React.useMemo(() => {
    return (queryResponse as any)?.results?.periods || (queryResponseCache as any)?.results?.periods || [];
  }, [queryResponse]);

  const primaryChartDataCalculator: {
    chartData: ChartData;
  } = React.useMemo(() => {
    const chartData: ChartData = { units: [], graphData: [], metricUnit: "" };

    try {
      // Collect datakeys
      for (const period of periods) {
        for (const group of period.groups || []) {
          const unitOfGroup = unitOf(group, pageView, groupBy, metric);

          if (chartData.units.some((cdu) => cdu.dataKey === unitOfGroup.dataKey)) {
            continue;
          }

          const unitIndex = chartData.units.length;
          const unit: ChartUnit = {
            dataKey: unitOfGroup.dataKey,
            gvc: unitOfGroup.gvc,
            label: unitOfGroup.label,
            primaryTotalSum: 0,
            secondaryTotalSum: 0,
            projectedTotalSum: 0,
            color: palette[unitIndex % palette.length],
          };
          chartData.units.push(unit);
        }
      }

      for (const periodIndex in periods) {
        const period = periods[periodIndex];
        const graphData: GraphData = {
          periodTimes: `${period.startTime} ${period.endTime}`,
          valueMap: {} as any,
          valueMapHidden: {} as any,
          valueOthers: { others: 0 },
          valueMapForDisplay: {} as any,
          primaryTotalMap: {} as any,
          primaryTotalMapHidden: {} as any,
          primaryTotalOthers: { others: 0 },
          primaryTotalMapForDisplay: {} as any,
          secondaryTotalMap: {} as any,
          secondaryTotalMapHidden: {} as any,
          secondaryTotalOthers: { others: 0 },
          secondaryTotalMapForDisplay: {} as any,
          projectedValueMap: {} as any,
          projectedValueMapHidden: {} as any,
          projectedValueOthers: { others: 0 },
          projectedValueMapForDisplay: {} as any,
          projectedTotalMap: {} as any,
          projectedTotalOthers: { others: 0 },
          projectedTotalMapHidden: {} as any,
          projectedTotalMapForDisplay: {} as any,
          primaryTotalMapOrderedRaw: [],
          secondaryTotalMapOrderedRaw: [],
          valueMapOrderedRaw: [],
        };

        // Sum total and values for the group from group consumptions, also update sums across the periods
        for (const group of period.groups || []) {
          // construct datakey
          const mergeMetrics = shouldMergeMetrics(pageView, groupBy);
          const forAccount = shouldProcessForAccount(groupBy);
          const { dataKey } = dataKeyOrgGVCOf(group, mergeMetrics, forAccount);

          // Set up keys in the maps
          if (!graphData.primaryTotalMap[dataKey]) {
            graphData.primaryTotalMap[dataKey] = 0;
          }
          if (!graphData.secondaryTotalMap[dataKey]) {
            graphData.secondaryTotalMap[dataKey] = 0;
          }
          if (!graphData.projectedTotalMap[dataKey]) {
            graphData.projectedTotalMap[dataKey] = 0;
          }
          if (!graphData.valueMap[dataKey]) {
            graphData.valueMap[dataKey] = 0;
          }
          if (!graphData.projectedValueMap[dataKey]) {
            graphData.projectedValueMap[dataKey] = 0;
          }

          for (const consumption of group.consumptions) {
            const total = consumption.total || 0;
            graphData.primaryTotalMap[dataKey] += total;
            const projectedTotal = consumption.projectedTotal || 0;
            let projectedTotalDiff = projectedTotal;
            if (projectedTotal > 0) {
              projectedTotalDiff = projectedTotal - total;
            }
            // TODO might rename this to something with diff and keep projectedTotal separate
            graphData.projectedTotalMap[dataKey] += projectedTotalDiff;

            const value = consumption.value || 0;
            graphData.valueMap[dataKey] += value;
            const projectedValue = consumption.projectedValue || 0;
            let projectedValueDiff = projectedValue;
            if (projectedValue > 0) {
              projectedValueDiff = projectedValue - value;
            }
            // TODO might rename this to something with diff and keep projectedValue separate
            graphData.projectedValueMap[dataKey] += projectedValueDiff;
          }
        }

        // fill empty keys' totals
        for (const dataKey of Object.keys(graphData.valueMap)) {
          if (!graphData.projectedValueMap[dataKey]) {
            graphData.projectedValueMap[dataKey] = 0;
          }
          if (!graphData.primaryTotalMap[dataKey]) {
            graphData.primaryTotalMap[dataKey] = 0;
          }
          if (!graphData.secondaryTotalMap[dataKey]) {
            graphData.secondaryTotalMap[dataKey] = 0;
          }
          if (!graphData.projectedTotalMap[dataKey]) {
            graphData.projectedTotalMap[dataKey] = 0;
          }
        }

        chartData.graphData.push(graphData);
      }

      // Process very big value numbers to readable numbers
      if (chargeableItemOfMetric) {
        chartData.metricUnit = chargeableItemOfMetric.unit;
      }
      const skipUnitProcessing = false;
      if (!skipUnitProcessing && pageView === "metric" && chargeableItemOfMetric) {
        let partToChange = "";
        let fromUnit = "";
        if (chargeableItemOfMetric.unit.includes("GB")) {
          partToChange = "GB";
          fromUnit = "GB";
        }
        if (chargeableItemOfMetric.unit.includes("Bytes")) {
          partToChange = "Bytes";
          fromUnit = "B";
        }
        if (chargeableItemOfMetric.unit.includes("bytes")) {
          partToChange = "bytes";
          fromUnit = "B";
        }

        if (fromUnit) {
          const allValues: number[] = [];
          for (const graphData of chartData.graphData) {
            allValues.push(...Object.values(graphData.valueMap));
          }

          if (DECIMAL_UNITS.includes(fromUnit as any)) {
            const decimalUnit = fromUnit as DECIMAL_UNIT;

            const allValuesBytes = DECIMAL_Helpers.convertInputArrayToBytes(allValues, decimalUnit);
            const bestUnit = DECIMAL_Helpers.getBestDisplayUnit(allValuesBytes);

            chartData.metricUnit = chargeableItemOfMetric.unit.replace(partToChange, bestUnit);

            for (const graphData of chartData.graphData) {
              for (const dataKey of Object.keys(graphData.valueMap)) {
                let newValue: number = DECIMAL_Helpers.convertBytesToUnit(
                  DECIMAL_Helpers.convertInputToBytes(graphData.valueMap[dataKey], decimalUnit),
                  bestUnit,
                );
                let newProjectedValue: number = DECIMAL_Helpers.convertBytesToUnit(
                  DECIMAL_Helpers.convertInputToBytes(graphData.projectedValueMap[dataKey], decimalUnit),
                  bestUnit,
                );

                graphData.valueMap[dataKey] = newValue;
                graphData.projectedValueMap[dataKey] = newProjectedValue;
              }
            }
          } else if (IEC_UNITS.includes(fromUnit as any)) {
            const iecUnit = fromUnit as IEC_UNIT;

            const allValuesBytes = IEC_Helpers.convertInputArrayToBytes(allValues, iecUnit);
            const bestUnit = IEC_Helpers.getBestDisplayUnit(allValuesBytes);

            chartData.metricUnit = chargeableItemOfMetric.unit.replace(partToChange, bestUnit);

            for (const graphData of chartData.graphData) {
              for (const dataKey of Object.keys(graphData.valueMap)) {
                let newValue: number = IEC_Helpers.convertBytesToUnit(
                  IEC_Helpers.convertInputToBytes(graphData.valueMap[dataKey], iecUnit),
                  bestUnit,
                );
                let newProjectedValue: number = IEC_Helpers.convertBytesToUnit(
                  IEC_Helpers.convertInputToBytes(graphData.projectedValueMap[dataKey], iecUnit),
                  bestUnit,
                );

                graphData.valueMap[dataKey] = newValue;
                graphData.projectedValueMap[dataKey] = newProjectedValue;
              }
            }
          }
        }
      }
    } catch (e) {
      console.log("primaryChartDataCalculator failed", e.message);
    }

    //
    return {
      chartData: chartData,
    };
  }, [periods]);

  const secondaryChartDataCalculator: { chartData: ChartData } = React.useMemo(() => {
    const chartData: ChartData = JSON.parse(JSON.stringify(primaryChartDataCalculator.chartData)) as any;
    try {
      if (!secondaryQueryResponse || pageView !== "cost" || groupBy !== "workloads") {
        return { chartData: chartData };
      }

      const periods: Period[] = (secondaryQueryResponse as any)?.results?.periods || [];
      for (const periodIndexStr in periods) {
        const periodIndex = Number(periodIndexStr);
        const period = periods[periodIndex];
        const periodTimes = `${period.startTime} ${period.endTime}`;
        const graphData = chartData.graphData.find((gd) => gd.periodTimes === periodTimes);

        if (!graphData) {
          continue;
        }

        for (const group of period.groups || []) {
          for (const consumption of group.consumptions) {
            const workloadName = consumption.tags.workload;
            if (!workloadName) {
              continue;
            }
            const dataKey = `${workloadName}-${group.key.gvc}`;

            const total = consumption.total || 0;
            if (!Object.keys(graphData.primaryTotalMap).includes(dataKey)) {
              continue;
            }
            graphData.secondaryTotalMap[dataKey] = graphData.primaryTotalMap[dataKey] + total;
            // projectedTotal is ignored for secondary costs
          }
        }

        for (const dataKey of Object.keys(graphData.primaryTotalMap)) {
          if (!graphData.secondaryTotalMap[dataKey]) {
            graphData.secondaryTotalMap[dataKey] = graphData.primaryTotalMap[dataKey];
          }
        }
      }

      // TODO fix if periods can have gaps
      if (periods.length < 1) {
        for (const graphData of chartData.graphData) {
          for (const dataKey of Object.keys(graphData.primaryTotalMap)) {
            graphData.secondaryTotalMap[dataKey] = graphData.primaryTotalMap[dataKey];
          }
        }
      }

      return { chartData: chartData };
    } catch (e) {
      return { chartData: chartData };
    }
  }, [primaryChartDataCalculator, pageView, groupBy, secondaryQueryResponse]);

  const unitSumCalculator: { chartData: ChartData } = React.useMemo(() => {
    const chartData = secondaryChartDataCalculator.chartData;
    for (const unit of chartData.units) {
      let primaryTotalSum = 0;
      let secondaryTotalSum = 0;
      let projectedTotalSum = 0;
      for (const graphData of chartData.graphData) {
        primaryTotalSum += graphData.primaryTotalMap[unit.dataKey] || 0;
        secondaryTotalSum += graphData.secondaryTotalMap[unit.dataKey] || 0;
        projectedTotalSum += graphData.projectedTotalMap[unit.dataKey] || 0;
      }
      unit.primaryTotalSum = primaryTotalSum;
      unit.secondaryTotalSum = secondaryTotalSum;
      unit.projectedTotalSum = projectedTotalSum;
    }
    return { chartData: chartData };
  }, [secondaryChartDataCalculator]);

  const dataKeysSorter: {
    dataKeysAllowed: string[];
    othersCount: number;
    hasHiddenDataKeys: boolean;
  } = React.useMemo(() => {
    const chartData = unitSumCalculator.chartData;

    const TOP_HOW_MANY = Number(topItemCount || defaultItemCount);

    // sort and decide which keys will be shown, which keys will be hidden
    const dataKeyPrimaryTotals: Record<string, number> = {};
    const dataKeySecondaryTotals: Record<string, number> = {};
    for (const graphDataIndexStr in chartData.graphData) {
      const grapDataIndex = Number(graphDataIndexStr);
      const graphData = chartData.graphData[grapDataIndex];
      for (const dataKey of Object.keys(graphData.primaryTotalMap)) {
        if (!dataKeyPrimaryTotals[dataKey]) {
          dataKeyPrimaryTotals[dataKey] = 0;
        }
        // ordering only by total, because more value should cost more total anyway
        dataKeyPrimaryTotals[dataKey] += graphData.primaryTotalMap[dataKey];
      }
      for (const dataKey of Object.keys(graphData.secondaryTotalMap)) {
        if (!dataKeySecondaryTotals[dataKey]) {
          dataKeySecondaryTotals[dataKey] = 0;
        }
        // ordering only by total, because more value should cost more total anyway
        dataKeySecondaryTotals[dataKey] += graphData.secondaryTotalMap[dataKey];
      }
    }
    const dataKeyPrimaryTotalsSorted = Object.entries(dataKeyPrimaryTotals).sort(([, a], [, b]) => b - a);
    const dataKeySecondaryTotalsSorted = Object.entries(dataKeySecondaryTotals).sort(([, a], [, b]) => b - a);
    let sortedToUse = dataKeyPrimaryTotalsSorted;
    if (pageView === "cost" && groupBy === "workloads" && shouldAddVolumesetCost) {
      sortedToUse = dataKeySecondaryTotalsSorted;
    }
    const dataKeysAllowed = sortedToUse.slice(0, TOP_HOW_MANY).map(([key]) => key);
    const othersCount = sortedToUse.length - dataKeysAllowed.length;
    const hasHiddenDataKeys = othersCount > 0;
    return {
      dataKeysAllowed: dataKeysAllowed,
      othersCount: othersCount,
      hasHiddenDataKeys: hasHiddenDataKeys,
    };
  }, [unitSumCalculator, pageView, groupBy, shouldAddVolumesetCost, topItemCount]);

  const displayMapsProcessor: { chartData: ChartData; dataKeysAllowed: string[] } = React.useMemo(() => {
    const chartData = JSON.parse(JSON.stringify(unitSumCalculator.chartData));
    const { dataKeysAllowed, othersCount, hasHiddenDataKeys } = dataKeysSorter;

    if (pageView === "cost") {
      // move data to hidden and others
      let othersPrimaryTotal = 0;
      let othersSecondaryTotal = 0;
      let othersProjectedTotal = 0;
      let othersValue = 0;
      let othersProjectedValue = 0;

      for (const graphDataIndexStr in chartData.graphData) {
        const graphDataIndex = Number(graphDataIndexStr);
        const graphData = chartData.graphData[graphDataIndex];

        for (const dataKey of Object.keys(graphData.primaryTotalMap)) {
          const primaryTotal = graphData.primaryTotalMap[dataKey] || 0;
          const secondaryTotal = graphData.secondaryTotalMap[dataKey] || 0;
          const projectedTotal = graphData.projectedTotalMap[dataKey] || 0;
          const value = graphData.valueMap[dataKey] || 0;
          const projectedValue = graphData.projectedValueMap[dataKey] || 0;

          graphData.primaryTotalMapOrderedRaw!.push([primaryTotal, dataKey]);
          graphData.secondaryTotalMapOrderedRaw!.push([secondaryTotal, dataKey]);
          graphData.valueMapOrderedRaw!.push([value, dataKey]);

          if (hasHiddenDataKeys && !dataKeysAllowed.includes(dataKey)) {
            graphData.primaryTotalOthers["others"] += primaryTotal;
            othersPrimaryTotal += primaryTotal;
            graphData.secondaryTotalOthers["others"] += secondaryTotal;
            othersSecondaryTotal += secondaryTotal;
            graphData.primaryTotalMapHidden[dataKey] = primaryTotal;
            graphData.secondaryTotalMapHidden[dataKey] = secondaryTotal;
            delete graphData.primaryTotalMap[dataKey];
            delete graphData.secondaryTotalMap[dataKey];

            graphData.valueOthers["others"] += value;
            othersValue += value;
            graphData.valueMapHidden[dataKey] = value;
            delete graphData.valueMap[dataKey];

            graphData.projectedTotalOthers["others"] += projectedTotal;
            othersProjectedTotal += projectedTotal;
            graphData.projectedTotalMapHidden[dataKey] = projectedTotal;
            delete graphData.projectedTotalMap[dataKey];

            graphData.projectedValueMap["others"] += projectedValue;
            othersProjectedValue += projectedValue;
            graphData.projectedValueMapHidden[dataKey] = projectedValue;
            delete graphData.projectedValueMap[dataKey];
          }
        }

        graphData.primaryTotalMapOrderedRaw!.sort((a, b) => b[0] - a[0]);
        graphData.secondaryTotalMapOrderedRaw!.sort((a, b) => b[0] - a[0]);
        graphData.valueMapOrderedRaw!.sort((a, b) => b[0] - a[0]);
      }

      if (hasHiddenDataKeys) {
        chartData.units.push({
          dataKey: "others",
          label: `Others (${othersCount} Entries)`,
          primaryTotalSum: othersPrimaryTotal,
          secondaryTotalSum: othersSecondaryTotal,
          projectedTotalSum: othersProjectedTotal,
          color: palette[0],
        });
      }
    }

    if (pageView === "metric") {
      let othersTotal = 0;
      let othersProjectedTotal = 0;
      let othersValue = 0;
      let othersProjectedValue = 0;

      for (const graphDataIndexStr in chartData.graphData) {
        const grapDataIndex = Number(graphDataIndexStr);
        const graphData = chartData.graphData[grapDataIndex];

        for (const dataKey of Object.keys(graphData.primaryTotalMap)) {
          const total = graphData.primaryTotalMap[dataKey] || 0;
          const value = graphData.valueMap[dataKey] || 0;
          const projectedTotal = graphData.projectedTotalMap[dataKey] || 0;
          const projectedValue = graphData.projectedValueMap[dataKey] || 0;

          graphData.primaryTotalMapOrderedRaw!.push([total, dataKey]);
          graphData.valueMapOrderedRaw!.push([value, dataKey]);

          if (hasHiddenDataKeys && !dataKeysAllowed.includes(dataKey)) {
            graphData.primaryTotalOthers["others"] += total;
            othersTotal += total;
            graphData.primaryTotalMapHidden[dataKey] = total;
            delete graphData.primaryTotalMap[dataKey];

            graphData.valueOthers["others"] += value;
            othersValue += value;
            graphData.valueMapHidden[dataKey] = value;
            delete graphData.valueMap[dataKey];

            graphData.projectedTotalOthers["others"] += projectedTotal;
            othersProjectedTotal += projectedTotal;
            graphData.projectedTotalMapHidden[dataKey] = projectedTotal;
            delete graphData.projectedTotalMap[dataKey];

            graphData.projectedValueMap["others"] += projectedValue;
            othersProjectedValue += projectedValue;
            graphData.projectedValueMapHidden[dataKey] = projectedValue;
            delete graphData.projectedValueMap[dataKey];
          }
        }

        graphData.primaryTotalMapOrderedRaw!.sort((a, b) => b[0] - a[0]);
        graphData.valueMapOrderedRaw!.sort((a, b) => b[0] - a[0]);
      }

      if (hasHiddenDataKeys) {
        chartData.units.push({
          dataKey: "others",
          label: `Others (${othersCount} Entries)`,
          metric: chargeableItemOfMetric?.consumptionTags.metric || "",
          projectedTotalSum: othersProjectedTotal,
          primaryTotalSum: othersTotal,
          secondaryTotalSum: 0,
          color: palette[0],
        });
      }
    }

    for (const graphDataIndexStr in chartData.graphData) {
      const grapDataIndex = Number(graphDataIndexStr);
      const graphData = chartData.graphData[grapDataIndex];

      graphData.primaryTotalMapForDisplay = graphData.primaryTotalMap;
      graphData.primaryTotalMapForDisplay["others"] = graphData.primaryTotalOthers["others"];

      // TODO check if we miss some datakeys here
      graphData.secondaryTotalMapForDisplay = graphData.secondaryTotalMap;
      graphData.secondaryTotalMapForDisplay["others"] = graphData.secondaryTotalOthers["others"];

      graphData.valueMapForDisplay = graphData.valueMap;
      graphData.valueMapForDisplay["others"] = graphData.valueOthers["others"];

      graphData.projectedTotalMapForDisplay = graphData.projectedTotalMap;
      graphData.projectedTotalMapForDisplay["others"] = graphData.projectedTotalOthers["others"];

      graphData.projectedValueMapForDisplay = graphData.projectedValueMap;
      graphData.projectedValueMapForDisplay["others"] = graphData.projectedValueOthers["others"];
    }

    return { chartData: chartData, dataKeysAllowed: dataKeysAllowed };
  }, [unitSumCalculator, dataKeysSorter]);
  const { chartData, dataKeysAllowed } = displayMapsProcessor;

  const chartDataUnits: ChartUnit[] = React.useMemo(() => {
    let chartDataUnits = JSON.parse(JSON.stringify(chartData.units));

    chartDataUnits = chartDataUnits.filter((unit) => [...dataKeysAllowed, "others"].includes(unit.dataKey));
    if (pageView === "cost") {
      chartDataUnits = chartDataUnits.filter((unit) => unit.metric !== "replicas");
    }
    chartDataUnits.sort((a, b) => {
      if (a[0] < b[0]) return -1;
      if (a[0] > b[0]) return 1;
      return 0;
    });

    // update chart data unit colors after all the filters
    for (const unitIndexStr in chartDataUnits) {
      const unitIndex = Number(unitIndexStr);
      const unit = chartDataUnits[unitIndex];
      unit.color = palette[unitIndex % palette.length];
    }
    return chartDataUnits;
  }, [displayMapsProcessor, palette]);

  const costCalculator: {
    totalSum: number;
    totalCost: string;
    projectedTotalSum: number;
    projectedTotalCost: string;
  } = React.useMemo(() => {
    let totalSum = 0;
    let projectedTotalSum = 0;

    try {
      for (const unit of chartDataUnits) {
        if (legendFilters.length < 1 || legendFilters.includes(unit.dataKey)) {
          if (pageView === "cost" && groupBy === "workloads" && shouldAddVolumesetCost) {
            totalSum += unit.secondaryTotalSum;
          } else {
            totalSum += unit.primaryTotalSum;
          }
          projectedTotalSum += unit.projectedTotalSum;
        }
      }
    } catch (e) {}

    const totalCost =
      "$" + new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalSum);
    const projectedTotalCost =
      "$" +
      new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
        totalSum + projectedTotalSum,
      );

    return {
      totalSum: totalSum,
      totalCost: totalCost,
      projectedTotalCost: projectedTotalCost,
      projectedTotalSum: projectedTotalSum,
    };
  }, [chartDataUnits, legendFilters, pageView, groupBy, shouldAddVolumesetCost]);
  const { totalSum, totalCost, projectedTotalSum, projectedTotalCost } = costCalculator;

  const hasAnyFilter = React.useMemo(() => {
    let hasAnyFilter =
      gvcFilters.length > 0 ||
      featureFilters.length > 0 ||
      locationFilters.length > 0 ||
      chargeableItemFilters.length > 0;
    if (!hasAnyFilter) {
      for (const [_, value] of Object.entries(tagFilters)) {
        if (value.length > 0) {
          hasAnyFilter = true;
        }
      }
    }
    return hasAnyFilter;
  }, [gvcFilters, featureFilters, locationFilters, chargeableItemFilters, tagFilters]);

  // label title options
  function getMetricOptions() {
    const metricOptions: { label: string; title: string; options: { label: string; value: string }[] }[] = [];
    for (const chargeableItem of BillingContext.ChargeableItems) {
      if (chargeableItem.type !== "consumption") {
        continue;
      }
      if (!chargeableItem.unit) {
        continue;
      }

      const feature = chargeableItem.consumptionTags["feature"];
      const found = metricOptions.find((o) => o.title === feature);
      if (!found) {
        metricOptions.push({
          label: feature,
          title: feature,
          options: [{ label: `${chargeableItem.name}`, value: chargeableItem.name }],
        });
      } else {
        found.options.push({
          label: `${chargeableItem.name}`,
          value: chargeableItem.name,
        });
      }
    }
    // this.Invoices = sortBy(data.invoices, (i) => i.startTime).reverse();
    for (const option of metricOptions) {
      option.options = sortBy(option.options, (i) => i.label);
    }
    return sortBy(metricOptions, (i) => i.label);
  }

  function getMetricGroupByOptions(): { label: string; value: string }[] {
    const options: { label: string; value: string }[] = [{ label: "None", value: "org" }];

    const selectedMetricOption = getMetricOptions().find((o) => o.options.some((_o) => _o.value === metric));
    if (selectedMetricOption) {
      const feature = selectedMetricOption.label;
      if (feature !== "mk8s") {
        options.push({ label: "GVC", value: "gvc" });
        options.push({ label: "Location", value: "location" });
      }
      options.push({
        label: featureLabelMap[feature] || feature,
        value: feature,
      });
    }
    return options;
  }

  return (
    <>
      <Layout key={org}>
        <div style={{ width: 1400 }}>
          {accountId && !isInGridView ? (
            <div className="flex items-center text-sm -mb-6">
              <button
                className="flex items-center gap-1 ngfocus color-link"
                onClick={() => navigate(`/billing${accountId ? `/account/-select?id=${accountId}` : ""}`)}
              >
                <ArrowRight className="feather-icon" />
                <span>Go to Billing</span>
              </button>
            </div>
          ) : null}
          <div className="flex items-center" style={{ width: 1370 }}>
            <div className={`text-2xl ${isInGridView ? "" : "mt-2"}`} style={{ width: isInGridView ? undefined : 136 }}>
              {pageView === "cost" ? "Cost" : "Usage"}
            </div>
            {isInGridView ? (
              <>
                <NGButton className="ml-4" size="small" variant="primary" onClick={() => setIsInGridView(false)}>
                  Show Graph
                </NGButton>
              </>
            ) : null}
            <div className="flex items-end gap-4 mb-4" style={{ display: isInGridView ? "none" : "" }}>
              <div className="flex flex-col">
                <span>
                  <FormLabel>Page View</FormLabel>
                </span>
                <Select
                  options={[
                    { label: "Cost", value: "cost" },
                    { label: "Usage", value: "metric" },
                  ]}
                  onChange={(value) => setPageView(value as any)}
                  value={pageView}
                />
              </div>
              {pageView === "cost" ? null : (
                <div className="flex flex-col">
                  <span>
                    <FormLabel>Metric</FormLabel>
                  </span>
                  <Select
                    style={{ width: 260 }}
                    value={metric}
                    onChange={(value) => setMetric(value)}
                    options={getMetricOptions()}
                  />
                </div>
              )}
              {chargeableItemOfMetric?.consumptionTags.feature === "observability" ? null : (
                <div className="flex flex-col">
                  <span>
                    <FormLabel>Group By</FormLabel>
                  </span>
                  <Select
                    style={{ width: 120 }}
                    options={
                      pageView === "cost"
                        ? [
                            { label: "None", value: "org" },
                            { label: "GVC", value: "gvc" },
                            { label: "Location", value: "location" },
                            { label: "Chargeable Item", value: "metric" },
                            { label: "Feature", value: "feature" },
                            ...features.map((feature) => ({
                              label: featureLabelMap[feature] || feature,
                              value: feature,
                            })),
                          ]
                        : getMetricGroupByOptions()
                    }
                    onChange={(value) => setGroupBy(value as any)}
                    value={groupBy}
                  />
                </div>
              )}
              <div className="flex flex-col">
                <span>
                  <FormLabel>Time Step</FormLabel>
                </span>
                <Select
                  style={{ width: 120 }}
                  value={timeStep}
                  options={[
                    { label: "Hour", value: "hour" },
                    { label: "Day", value: "day" },
                    { label: "Week", value: "week" },
                    { label: "Month", value: "month" },
                  ]}
                  onChange={(value) => {
                    if (!mounted) {
                      return;
                    }

                    let newEndTime = moment().add(1, "day").startOf("day");
                    let newStartTime = newEndTime.clone();
                    if (value === "hour") {
                      newStartTime.subtract(1, "day");
                    }
                    if (value === "day") {
                      newStartTime.subtract(31, "day");
                    }
                    if (value === "week") {
                      newStartTime.subtract(5, "week");
                    }
                    if (value === "month") {
                      newStartTime.subtract(1, "year");
                    }
                    setStartISO(newStartTime.toISOString());
                    setEndISO(newEndTime.toISOString());
                    setTimeStep(value as any);
                  }}
                />
              </div>
              <div className="flex flex-col">
                <span>
                  <FormLabel>Time Range ({Timezone.label})</FormLabel>
                </span>
                <RangePicker
                  size="small"
                  clearIcon={false}
                  fromISO={startISO}
                  setFromISO={setStartISO}
                  toISO={endISO}
                  setToISO={setEndISO}
                  showTime={timeStep === "hour"}
                  showHour={timeStep === "hour"}
                  showMinute={false}
                  showSecond={false}
                  allowEmpty={[false, false]}
                  presets={
                    timeStep === "hour"
                      ? getUsageHourPresets()
                      : timeStep === "day"
                      ? getUsageDayPreset()
                      : timeStep === "week"
                      ? getUsageWeekPreset()
                      : getUsageMonthPreset()
                  }
                  timezoneValue={Timezone.value}
                />
              </div>
              <div ref={dropdownRef}>
                <Dropdown
                  open={isFiltersOpen}
                  onOpenChange={handleDropdownOpenChange}
                  dropdownRender={() => (
                    <div
                      id="usage-filter-dropdown"
                      className="dropdown overflow-auto"
                      style={{ maxHeight: "calc(90svh - 120px)" }}
                      onClick={(e: any) => {
                        // Prevent the click event from propagating up to the document
                        e.stopPropagation();
                      }}
                    >
                      <div className="dropdown-item">
                        <div className="flex items-center">
                          <label>Max Visible Item Count</label>
                          <InfoTooltip
                            title={[
                              `Limits the number of items shown in the graph, rest of the items are grouped under the label "Others"`,
                              `You can still see all the items in the "Spreadsheet" view under Actions > Spreadsheet View`,
                              `Items are sorted based on the total cost or metric value accumulated over the selected time period.`,
                            ]}
                          />
                        </div>
                        <Slider
                          value={topItemCountRaw || defaultItemCount}
                          onChange={setTopItemCountRaw}
                          marks={{
                            50: "50",
                            100: "100",
                            200: "200",
                            300: "300",
                            400: "400",
                          }}
                          min={10}
                          step={10}
                          defaultValue={defaultItemCount}
                          max={500}
                        />
                      </div>
                      {pageView === "metric" ? null : (
                        <>
                          <div className="dropdown-item">
                            <label>Features</label>
                            <Select
                              mode="multiple"
                              value={featureFilters}
                              placeholder={"All"}
                              options={features.map((f) => ({ label: featureLabelMap[f], value: f }))}
                              onChange={setFeatureFilters}
                            />
                          </div>
                          <div className="dropdown-item">
                            <label>Chargeable Items</label>
                            <Select
                              mode="multiple"
                              value={chargeableItemFilters}
                              placeholder={"All"}
                              filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                              }
                              options={cItems.map((c) => ({ label: c.name, value: c.id }))}
                              onChange={setChargeableItemFilters}
                            />
                          </div>
                        </>
                      )}
                      {pageView === "cost" ||
                      (pageView === "metric" && getMetricGroupByOptions().some((o) => o.value === "gvc")) ? (
                        <div className="dropdown-item">
                          <label>GVCs</label>
                          <Select
                            mode={"multiple"}
                            value={gvcFilters}
                            placeholder={"All"}
                            onChange={setGVCFilters}
                            options={allGVCs.map((g) => ({ label: g, value: g }))}
                          />
                        </div>
                      ) : null}
                      {pageView === "cost" ||
                      (pageView === "metric" && getMetricGroupByOptions().some((o) => o.value === "location")) ? (
                        <div className="dropdown-item">
                          <label>Locations</label>
                          <Select
                            mode={"multiple"}
                            value={locationFilters}
                            placeholder={"All"}
                            onChange={setLocationFilters}
                            options={allLocationss.map((g) => ({ label: g, value: g }))}
                          />
                        </div>
                      ) : null}
                      {[...features]
                        .filter((feature) => {
                          if (pageView === "cost") {
                            return true;
                          }

                          return getMetricGroupByOptions().some((o) => o.value === feature);
                        })
                        .map((tag) => (
                          <div className="dropdown-item">
                            <label>{featureLabelMap[tag] || tag}</label>
                            <Select
                              mode={"multiple"}
                              value={tagFilters[tag] || []}
                              placeholder={"All"}
                              onChange={(filters) => {
                                const _tagFilters = JSON.parse(JSON.stringify(tagFilters));
                                _tagFilters[tag] = filters;
                                setTagFilters(_tagFilters);
                              }}
                              options={(allTags[tag] || []).map((t) => ({ label: t, value: t }))}
                            />
                          </div>
                        ))}
                      <div className="dropdown-actions">
                        {hasAnyFilter ? (
                          <NGButton
                            variant="secondary"
                            text
                            size={"normal"}
                            onClick={() => {
                              const _tagFilters = JSON.parse(JSON.stringify(tagFilters));
                              for (const key of Object.keys(_tagFilters)) {
                                _tagFilters[key] = [];
                              }
                              setTagFilters(_tagFilters);
                              setGVCFilters([]);
                              setLocationFilters([]);
                              setFeatureFilters([]);
                              setChargeableItemFilters([]);
                            }}
                          >
                            Clear Filters
                          </NGButton>
                        ) : null}
                        <NGButton size={"normal"} variant="primary" onClick={() => setIsFiltersOpen(false)}>
                          Ok
                        </NGButton>
                      </div>
                    </div>
                  )}
                  trigger={[]}
                >
                  <NGButton
                    style={{ marginTop: "auto" }}
                    size={"normal"}
                    variant={hasAnyFilter ? "primary" : "secondary"}
                    outlined={!hasAnyFilter}
                    renderIcon={(hover, props) =>
                      hasAnyFilter ? (
                        <Check {...props} size={18} />
                      ) : isFiltersOpen ? (
                        <ChevronUp {...props} size={18} />
                      ) : (
                        <ChevronDown {...props} size={18} />
                      )
                    }
                    onClick={() => setIsFiltersOpen((x) => !x)}
                  >
                    Filters
                  </NGButton>
                </Dropdown>
              </div>
              {pageView === "cost" && groupBy === "workloads" ? (
                <NGCheckbox checked={shouldAddVolumesetCost} onChange={(v) => setShouldAddVolumesetCost(v)}>
                  <div className="flex items-center">
                    <span>Volume Sets</span>
                    <InfoTooltip
                      title={[
                        `Includes the cost for volume sets that are connected to the workloads.`,
                        `This will not include capacity cost for volume sets with "shared" performance class.`,
                        `Volume Sets with "shared" performance class also have cpu/memory costs, which are not included here.`,
                      ]}
                    />
                  </div>
                </NGCheckbox>
              ) : null}
            </div>
            <span className="flex-grow" />
            <Dropdown
              trigger={["click"]}
              dropdownRender={() => (
                <Menu
                  className="menu"
                  onClick={async ({ key }: any) => {
                    switch (key) {
                      case "grid-view":
                        setIsInGridView(true);
                        break;
                      case "view":
                        setIsViewing(true);
                        break;
                      case "export-spreadsheet":
                        exportSpreadsheet();
                        break;
                      case "export-json":
                        await exportJSON(false);
                        break;
                      case "export-json-detailed":
                        await exportJSON(true);
                        break;
                    }
                  }}
                >
                  {isInGridView ? null : (
                    <Menu.Item disabled={!queryResponse} key={"grid-view"} data-testid="action-grid-view">
                      Spreadsheet View
                    </Menu.Item>
                  )}
                  <Menu.Item disabled={!queryResponse} key={"view"} data-testid="action-view">
                    View
                  </Menu.Item>
                  <Menu.SubMenu title={"Export"} key={"ignore"} data-testid="action-export">
                    <Menu.Item key={"export-spreadsheet"} data-testid="action-export-spreadsheet">
                      Spreadsheet
                    </Menu.Item>
                    <Menu.Item key={"export-json"} data-testid="action-export-json">
                      JSON
                    </Menu.Item>
                    <Menu.Item key={"export-json-detailed"} data-testid="action-export-json-detailed">
                      JSON Detailed
                    </Menu.Item>
                  </Menu.SubMenu>
                </Menu>
              )}
            >
              <NGButton
                variant="primary"
                outlined
                size={"normal"}
                className="mt-auto mb-4"
                renderIcon={(hover, props) => <ChevronDown {...props} size={18} />}
                loading={isLoading || isLoadingViewExport}
                disabled={isLoading || isLoadingViewExport}
                data-testid="usage-actions"
              >
                Actions
              </NGButton>
            </Dropdown>
          </div>
          {isInGridView ? (
            <UsageSpreadsheetView
              isLoading={isLoading}
              chartData={chartData}
              groupBy={groupBy}
              timeStep={timeStep}
              shouldAddVolumesetCost={shouldAddVolumesetCost}
              pageView={pageView}
              isBillingDashboard={false}
            />
          ) : null}
          <div style={{ width: 1370, display: isInGridView ? "none" : "block" }} className="mb-24 relative">
            {error ? <NGAlert type={"error"} message={error} /> : null}
            {!error ? (
              <>
                <div
                  className={`relative ${!!queryResponse && Object.keys(queryResponse).length < 1 ? "usage-blur" : ""}`}
                >
                  <BarChart
                    className={`${isLoading ? "pointer-events-none" : ""}`}
                    key={queryResponseKey}
                    width={1370}
                    height={450}
                    data={chartData.graphData}
                    barCategoryGap={"15%"}
                    margin={{ left: 0, top: 50 }}
                    barGap={chartDataUnits.length < 5 ? undefined : 0}
                    onMouseMove={(p: OnMouseMoveParam) => {
                      if (tooltipData?.activeLabel !== p?.activeLabel) {
                        setTooltipData(p);
                      }
                      if (p && p.activeTooltipIndex !== undefined) {
                        setActiveBarIndex(p.activeTooltipIndex);
                      } else {
                        setActiveBarIndex(null);
                      }
                    }}
                    onMouseLeave={() => setActiveBarIndex(null)}
                  >
                    <CartesianGrid
                      vertical={false}
                      strokeDasharray="3 6"
                      fill={theme === "dark" ? "#333" : "#eee"}
                      fillOpacity={0.6}
                      stroke={theme === "dark" ? "#ccc" : "#888"}
                    />
                    {pageView === "cost" && projectedTotalSum > 0 ? (
                      <XAxis
                        label={{
                          value: `Total Projected Cost: ${projectedTotalCost}`,
                          position: "top",
                        }}
                        axisLine={false}
                        tickLine={false}
                        type={"number"}
                        xAxisId={"projectedTotalCost"}
                        orientation={"top"}
                        tickFormatter={(_v: string) => ""}
                      />
                    ) : null}
                    {pageView === "cost" ? (
                      <XAxis
                        label={{
                          value: `Total Cost: ${totalCost}`,
                          position: "top",
                        }}
                        axisLine={false}
                        tickLine={false}
                        type={"number"}
                        xAxisId={"totalCost"}
                        orientation={"top"}
                        tickFormatter={(_v: string) => ""}
                      />
                    ) : null}
                    <XAxis
                      tickFormatter={(value) =>
                        periods.length < 1
                          ? "No Data"
                          : `${formatXLabel({
                              value: value,
                              timeStep: timeStep,
                            })}`
                      }
                      dataKey={"periodTimes"}
                      tickLine={false}
                      orientation={"bottom"}
                    />
                    <YAxis
                      label={{
                        value: pageView === "cost" ? "Cost" : "",
                        offset: 20,
                        position: "top",
                      }}
                      tickFormatter={(value: any) => {
                        let processedValue = value;
                        try {
                          processedValue = numeral(value).format("0a");
                          if (!processedValue.includes("k") && !processedValue.includes("m")) {
                            processedValue = numeral(value).format("0.0a");
                            if (processedValue.replaceAll(".", "").length < 4) {
                              processedValue = numeral(value).format("0.00a");
                            }
                          }
                        } catch (e) {
                          processedValue = `${value}RR`;
                        }
                        return `${pageView === "cost" ? "$" : ""}${processedValue}`;
                      }}
                      axisLine={true}
                      tickLine={false}
                      type={"number"}
                      yAxisId="left"
                    />
                    <Bar
                      dataKey={"cpln-placeholder-hover"}
                      isAnimationActive={false}
                      yAxisId="left"
                      shape={({ x, index }) => (
                        <rect
                          x={x}
                          y={chartData.graphData.length > 1 && totalSum > 0 ? (projectedTotalSum > 0 ? 110 : 80) : 80}
                          width={1310 / chartData.graphData.length}
                          height={
                            chartData.graphData.length > 1 && totalSum > 0 ? (projectedTotalSum > 0 ? 310 : 340) : 340
                          }
                          fill={
                            index === activeBarIndex
                              ? theme === "dark"
                                ? "rgba(255, 255, 255, 0.1)"
                                : "rgba(0, 0, 0, 0.1)"
                              : "transparent"
                          }
                          rx={0}
                          ry={0}
                        />
                      )}
                    />
                    {chartDataUnits.map((unit, index) => {
                      return (
                        <Bar
                          key={unit.dataKey}
                          unit={pageView === "cost" ? "$" : ""}
                          dataKey={`${
                            pageView === "cost"
                              ? groupBy === "workloads" && shouldAddVolumesetCost
                                ? "secondaryTotalMapForDisplay"
                                : "primaryTotalMapForDisplay"
                              : "valueMapForDisplay"
                          }.${unit.dataKey}`}
                          fill={unit.color}
                          yAxisId={"left"}
                          stackId={unit.dataKey}
                          hide={legendFilters.length > 0 && !legendFilters.includes(unit.dataKey)}
                        />
                      );
                    })}
                    {chartDataUnits.map((unit, index) => {
                      return (
                        <Bar
                          key={unit.dataKey + "projected"}
                          unit={pageView === "cost" ? "$" : ""}
                          dataKey={`${
                            pageView === "cost" ? "projectedTotalMapForDisplay" : "projectedValueMapForDisplay"
                          }.${unit.dataKey}`}
                          stroke={unit.color}
                          fill="transparent"
                          yAxisId={"left"}
                          stackId={unit.dataKey}
                          hide={legendFilters.length > 0 && !legendFilters.includes(unit.dataKey)}
                        />
                      );
                    })}
                    <Legend
                      content={<LegendPayloadSaver setPayload={setLegendPayload} />}
                      wrapperStyle={{
                        bottom: -10,
                        right: 0,
                      }}
                    />
                  </BarChart>
                  <CustomLegend
                    filters={legendFilters}
                    chartDataUnits={chartDataUnits}
                    groupBy={groupBy}
                    onClick={toggleLegendFilter}
                    payload={legendPayload}
                  />
                  <CardTooltip
                    data={tooltipData}
                    drillDown={drillDown}
                    timeStep={timeStep}
                    groupBy={groupBy}
                    pageView={pageView}
                    chartData={chartData}
                  />
                  <NGAlert
                    style={{ width: 650, zIndex: 1 }}
                    type={"info"}
                    message={"All charges are estimated, the final bill is the authoritative accurate source."}
                  />
                  <div className="flex flex-col items-center px-32 mt-4">
                    <div className="flex items-center">
                      <div className="font-semibold pl-6">Chargeable Items</div>
                      <InfoTooltip
                        title={[
                          "Control Plane tracks usage and cost with metered chargeable items.",
                          "You can see which items are taken into account depending on your selections and filters.",
                          "All items are not always included in this page for filtering purposes, because not all items apply to grouping options such as gvc, location, workload etc.",
                        ]}
                      />
                    </div>
                    {Object.entries(
                      lodashGroupBy(
                        BillingContext.ChargeableItems.filter(
                          (ci) => !!ci.consumptionTags.feature && ci.type === "consumption",
                        ),
                        (ci) => ci.consumptionTags.feature,
                      ),
                    )
                      .sort()
                      .map(([feature, items]) => (
                        <div className="flex flex-col items-center my-2">
                          <div className="font-semibold text-sm">{feature}</div>
                          <div className="flex items-center justify-center gap-x-4 gap-y-1 flex-wrap">
                            {items.map((item) => {
                              let used = false;
                              let partlyUsed = false;
                              if (pageView === "cost" && groupBy === "workloads" && shouldAddVolumesetCost) {
                                used =
                                  !!queryRequestBodiesRaw.vsBody &&
                                  queryRequestBodiesRaw.vsBody.consumptionQueries.some((cq) => cq.id === item.id);
                                if (used) {
                                  partlyUsed = true;
                                }
                              }
                              if (!used) {
                                used = queryRequestBodiesRaw.body.consumptionQueries.some((cq) => cq.id === item.id);
                              }
                              if (used && !partlyUsed) {
                                const cq = queryRequestBodiesRaw.body.consumptionQueries.find(
                                  (cq) => cq.id === item.id,
                                );
                                if (cq) {
                                  if (cq.filterBy.name || cq.filterBy.gvc || cq.filterBy.location) {
                                    partlyUsed = true;
                                  }
                                }
                              }
                              const color = partlyUsed ? "orange" : used ? "green" : "red";

                              return (
                                <div className="flex items-center text-xs">
                                  {partlyUsed ? (
                                    <Circle style={{ color: color }} className="feather-icon" />
                                  ) : used ? (
                                    <Check style={{ color: color }} className="feather-icon" />
                                  ) : (
                                    <X style={{ color: color }} className="feather-icon" />
                                  )}
                                  {/* <div style={{ color: color }}>{used ? "Y" : "N"}</div> */}
                                  <div>{item.name}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    <div className="text-sm mt-4">
                      <div className="flex items-center gap-2">
                        <Check style={{ color: "green" }} className="feather-icon" />
                        <div>All results of this chargeable item are included.</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Circle style={{ color: "orange" }} className="feather-icon" />
                        <div>Some results of this chargeable item are included.</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <X style={{ color: "red" }} className="feather-icon" />
                        <div>Results of this chargeable item are not included.</div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
            {!error && isLoading ? (
              <div className="absolute w-full flex items-center justify-center top-0 mt-64">
                <Loader reason={"fetching billing details"} />
              </div>
            ) : null}
          </div>
          {isViewing ? (
            <ViewModal
              filename={`usage-${org}-${endISO}-${timeStep}.json`}
              onClose={() => setIsViewing(false)}
              title={"Cost & Usage"}
              visible={isViewing}
              initialFormat={"json"}
              object={queryResponse}
            />
          ) : null}
        </div>
      </Layout>
    </>
  );
};

export default observer(UsageNGRaw);
